<template>
  <section class="banner-panel">
    <div class="banner-panel-head">
      <h3 class="banner-panel-title">轮播图</h3>
      <button type="button" class="btn btn-primary" :disabled="!canEdit" @click="openCreate">新增轮播</button>
    </div>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div v-if="sortedList.length" class="carousel-shell">
      <button type="button" class="carousel-nav" :disabled="carouselIndex <= 0" @click="shiftCarousel(-1)">‹</button>
      <div class="carousel-viewport">
        <div class="carousel-track" :style="trackStyle">
          <div
            v-for="item in sortedList"
            :key="item.id"
            class="carousel-card"
            :class="{ 'carousel-card--active': item.id === activeItem?.id }"
            @click="openEdit(item)"
          >
            <img class="carousel-img" :src="toFullUrl(item.imageUrl)" alt="banner" @error="onImgError">
            <button
              type="button"
              class="carousel-delete"
              :disabled="!canDelete"
              title="删除"
              @click.stop="onDelete(item)"
            >×</button>
            <div v-if="item.statusCode !== 'ENABLED'" class="carousel-badge">已停用</div>
          </div>
        </div>
      </div>
      <button
        type="button"
        class="carousel-nav"
        :disabled="carouselIndex >= sortedList.length - 1"
        @click="shiftCarousel(1)"
      >›</button>
    </div>
    <p v-else class="muted">暂无轮播，点击「新增轮播」添加</p>
    <div v-if="sortedList.length" class="carousel-dots">
      <button
        v-for="(item, idx) in sortedList"
        :key="`dot-${item.id}`"
        type="button"
        class="carousel-dot"
        :class="{ active: idx === carouselIndex }"
        @click="carouselIndex = idx"
      />
    </div>

    <div v-if="showDialog" class="mask" @click.self="closeDialog">
      <div class="dialog dialog--shell dialog--md" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ editId ? '编辑轮播图' : '新增轮播图' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
        <div class="form">
          <div class="field field-upload">
            <span>轮播图片</span>
            <input ref="fileInput" class="hidden-input" type="file" accept="image/png,image/jpeg,image/jpg" @change="onFileChange">
            <div class="upload-box" @click="pickImage">
              <template v-if="form.imageUrl">
                <img class="upload-preview-img" :src="toFullUrl(form.imageUrl)" alt="preview">
              </template>
              <template v-else>
                <div class="upload-plus">+</div>
                <div class="upload-text">360×180</div>
              </template>
            </div>
            <small class="upload-hint">请上传大小不超过 20MB 格式为 .png .jpg .jpeg 图片</small>
            <small v-if="uploadStatusMsg" class="upload-status">{{ uploadStatusMsg }}</small>
          </div>
          <label class="field full">
            <span>跳转类型</span>
            <div class="link-kind-row">
              <button
                type="button"
                class="btn btn-edit btn-sm"
                :class="{ active: form.linkKind === 'PAGE' }"
                @click="setLinkKind('PAGE')"
              >小程序页面</button>
              <button
                type="button"
                class="btn btn-edit btn-sm"
                :class="{ active: form.linkKind === 'WECHAT' }"
                @click="setLinkKind('WECHAT')"
              >公众号文章</button>
            </div>
          </label>
          <label v-if="form.linkKind === 'PAGE'" class="field full">
            <span>跳转路径</span>
            <FancySelect
              v-model="form.linkTarget"
              class="field-select"
              :options="targetSelectOptions"
              placeholder="请选择"
              searchable
              @change="onTargetChange"
            />
          </label>
          <label v-else class="field full">
            <span>公众号链接</span>
            <input
              v-model.trim="form.linkTarget"
              class="link-url-input"
              placeholder="https://mp.weixin.qq.com/s/..."
            >
          </label>
          <label class="field">
            <span>排序</span>
            <input v-model.number="form.sortOrder" type="number" min="1" placeholder="请输入">
          </label>
          <label class="field field-switch">
            <span>启用</span>
            <StatusSwitch v-model="statusEnabled" :disabled="!canEdit" />
          </label>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="!canEdit" @click="onSubmit">确定</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import StatusSwitch from '@/components/StatusSwitch.vue'
import { BASE_URL, createBanner, deleteBanner, getBanners, getPageTargetCandidates, updateBanner, uploadImage } from '@/api'
import { hasAnyPermission } from '@/utils/adminPermission'
import { createEmptyBannerForm, inferBannerLinkKind, isWechatArticleUrl, mapPageTargetRows, normalizeTargetValue } from '@/utils/bannerAdmin'

export default {
  name: 'BannerCarouselPanel',
  components: { FancySelect, StatusSwitch },
  data() {
    return {
      list: [],
      errorMsg: '',
      showDialog: false,
      editId: null,
      form: createEmptyBannerForm(),
      canEdit: false,
      canDelete: false,
      pageTargetOptions: [],
      imagePickBusy: false,
      uploadStatusMsg: '',
      carouselIndex: 0,
    }
  },
  computed: {
    sortedList() {
      return (this.list || [])
        .slice()
        .sort((a, b) => (Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) || (Number(a.id || 0) - Number(b.id || 0)))
    },
    activeItem() {
      return this.sortedList[this.carouselIndex] || null
    },
    trackStyle() {
      const n = this.sortedList.length
      if (!n) return {}
      return { transform: `translateX(-${this.carouselIndex * 100}%)` }
    },
    targetSelectOptions() {
      return (this.pageTargetOptions || []).map((o) => ({ value: o.value, label: o.label || o.value }))
    },
    statusEnabled: {
      get() {
        return String(this.form.statusCode || '').toUpperCase() === 'ENABLED'
      },
      set(v) {
        this.form.statusCode = v ? 'ENABLED' : 'DISABLED'
      },
    },
  },
  mounted() {
    this.canEdit = hasAnyPermission(['cms.manage', 'cms.edit'])
    this.canDelete = hasAnyPermission(['cms.manage', 'cms.delete'])
    this.load()
    this.loadPageTargets()
  },
  methods: {
    async loadPageTargets() {
      try {
        const rows = await getPageTargetCandidates()
        this.pageTargetOptions = mapPageTargetRows(rows)
      } catch {
        this.pageTargetOptions = []
      }
    },
    async load() {
      try {
        this.list = (await getBanners()) || []
        if (this.carouselIndex >= this.sortedList.length) {
          this.carouselIndex = Math.max(0, this.sortedList.length - 1)
        }
      } catch (e) {
        this.errorMsg = e.message || '读取轮播失败'
      }
    },
    shiftCarousel(delta) {
      const next = this.carouselIndex + delta
      if (next < 0 || next >= this.sortedList.length) return
      this.carouselIndex = next
    },
    openCreate() {
      this.editId = null
      this.uploadStatusMsg = ''
      const maxSort = (this.list || []).reduce((m, x) => Math.max(m, Number(x?.sortOrder || 0)), 0)
      this.form = { ...createEmptyBannerForm(), sortOrder: maxSort + 1, statusCode: 'ENABLED', linkKind: 'PAGE' }
      this.showDialog = true
    },
    openEdit(item) {
      if (!item) return
      this.uploadStatusMsg = ''
      this.editId = item.id
      const linkTarget = normalizeTargetValue(item?.linkTarget)
      this.form = {
        ...createEmptyBannerForm(),
        ...item,
        linkTarget,
        linkKind: inferBannerLinkKind(linkTarget),
      }
      const idx = this.sortedList.findIndex((x) => x.id === item.id)
      if (idx >= 0) this.carouselIndex = idx
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
    },
    setLinkKind(kind) {
      const next = kind === 'WECHAT' ? 'WECHAT' : 'PAGE'
      if (this.form.linkKind === next) return
      this.form.linkKind = next
      this.form.pageTargetId = null
      if (next === 'WECHAT') {
        this.form.linkTarget = isWechatArticleUrl(this.form.linkTarget) ? this.form.linkTarget : ''
      } else {
        this.form.linkTarget = isWechatArticleUrl(this.form.linkTarget) ? '' : normalizeTargetValue(this.form.linkTarget)
      }
    },
    onTargetChange() {
      const linkTarget = normalizeTargetValue(this.form.linkTarget)
      const hit = (this.pageTargetOptions || []).find((o) => o.value === linkTarget)
      this.form.pageTargetId = hit && Number.isFinite(hit.id) ? hit.id : null
    },
    pickImage() {
      if (this.imagePickBusy || !this.canEdit) return
      this.imagePickBusy = true
      const input = this.$refs.fileInput
      if (input) input.click()
      window.setTimeout(() => { this.imagePickBusy = false }, 300)
    },
    async onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      if (file.size > 20 * 1024 * 1024) {
        this.errorMsg = '图片不能超过 20MB'
        e.target.value = ''
        return
      }
      try {
        this.uploadStatusMsg = ''
        const data = await uploadImage(file)
        this.form.imageUrl = data.url || ''
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },
    async onSubmit() {
      try {
        if (!this.form.imageUrl) throw new Error('请上传轮播图片')
        let linkTarget = String(this.form.linkTarget || '').trim()
        if (this.form.linkKind === 'WECHAT') {
          if (!isWechatArticleUrl(linkTarget)) {
            throw new Error('请填写有效的公众号文章链接（mp.weixin.qq.com）')
          }
        } else {
          linkTarget = normalizeTargetValue(linkTarget)
          if (!linkTarget) throw new Error('请选择跳转路径')
          const ok = this.pageTargetOptions.some((i) => i.value === linkTarget)
          if (!ok) throw new Error('请选择有效跳转路径')
          this.form.linkTarget = linkTarget
          this.onTargetChange()
        }
        this.form.linkTarget = linkTarget
        const payload = {
          title: this.form.title || '',
          imageUrl: this.form.imageUrl,
          linkTarget,
          pageTargetId: this.form.linkKind === 'WECHAT' ? null : this.form.pageTargetId,
          sortOrder: this.form.sortOrder,
          statusCode: this.form.statusCode,
          openToNonMember: this.form.openToNonMember,
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          remark: this.form.remark || '',
        }
        const wasEdit = !!this.editId
        if (this.editId) await updateBanner(this.editId, payload)
        else await createBanner(payload)
        this.showDialog = false
        await this.load()
        if (!wasEdit) this.carouselIndex = Math.max(0, this.sortedList.length - 1)
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      }
    },
    async onDelete(item) {
      if (!item || !await adminConfirm('确定删除该轮播图吗？')) return
      try {
        await deleteBanner(item.id)
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    toFullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url
      return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
    },
    onImgError() {
      /* ignore */
    },
  },
}
</script>

<style scoped>
.banner-panel {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #edf0f5;
  border-radius: 10px;
}
.banner-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.banner-panel-title {
  margin: 0;
  font-size: 15px;
  color: #1d2939;
}
.carousel-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.carousel-viewport {
  flex: 0 0 360px;
  width: 360px;
  max-width: 100%;
  overflow: hidden;
  min-height: 180px;
}
.carousel-track {
  display: flex;
  transition: transform 0.28s ease;
  padding: 4px 0;
}
.carousel-card {
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 180px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #1e3a5f;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
.carousel-card--active {
  outline: 2px solid #2f6af6;
  outline-offset: 2px;
}
.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.carousel-delete {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.carousel-delete:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.carousel-badge {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
}
.carousel-nav {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.carousel-nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}
.carousel-dot {
  width: 28px;
  height: 4px;
  border: none;
  border-radius: 999px;
  background: #d0d7e2;
  cursor: pointer;
  padding: 0;
}
.carousel-dot.active {
  background: #2f6af6;
}
.btn {
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 13px;
}
.btn-primary { background: #2f6af6; color: #fff; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.btn-edit {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.btn-edit.active {
  border-color: #2f6af6;
  background: #eef4ff;
  color: #2f6af6;
}
.btn-sm { padding: 6px 12px; font-size: 12px; }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.link-kind-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.link-url-input { width: 100%; }
.error { color: #d92d20; font-size: 13px; margin: 0 0 8px; }
.muted { color: #98a2b3; font-size: 13px; text-align: center; margin: 12px 0; }
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}
.field > span { font-weight: 600; }
.field.full,
.field-upload { grid-column: span 2; }
.field-switch {
  flex-direction: row;
  align-items: center;
  gap: 12px;
}
.field-select { width: 100%; }
:deep(.field-select .fs-btn) {
  width: 100%;
  height: 40px;
  border-radius: 8px;
}
input {
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  height: 40px;
  box-sizing: border-box;
}
.hidden-input { display: none; }
.upload-box {
  width: 360px;
  max-width: 100%;
  height: 180px;
  border: 1px dashed #d0d7e2;
  background: #fafbff;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.upload-plus {
  font-size: 36px;
  color: #98a2b3;
  line-height: 1;
}
.upload-text { color: #667085; font-size: 12px; }
.upload-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}
.upload-hint { color: #98a2b3; font-size: 12px; }
.upload-status { color: #067647; font-size: 12px; }
</style>
